import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import Message from '@ui/message/Message';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import { GuestMixSplit } from '@app/model/bi/Bi';
import { useGuestMixEvolution, useGuestMixReport } from '@app/model/bi/service/BiHooks';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import {
	EvolutionView, GuestMixEvolutionCard, GuestMixFocusCard, GuestMixHeadline, GuestMixMixCard, GuestMixTableCard,
} from '../fragment/GuestMixCards';
import { buildTable, ColumnSet, EvolutionMeasure, exportColumns, headline, splitInfo, splitOf, SPLITS } from '../fragment/GuestMix.utilities';
import { exportFileName, formatDateTime, periodPresets, propertyCount, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';
import { useBiWidthClass } from '../BiFullWidth';
import BiSwitchRow from '../fragment/BiSwitchRow';
import useColumnSets from '../useColumnSets';
import { adrRulesNote, scopeRulesNote } from '@app/model/integration-settings/ReportRules';

// Decision 6: TODAY (always), PREVIOUS YEAR and PU 7.
const DEFAULT_SETS: ColumnSet[] = ['ly', 'pu7'];
const ALL_SETS: ColumnSet[] = ['ly', 'd1', 'd3', 'd7', 'd14', 'd30', 'pu1', 'pu3', 'pu7', 'pu14', 'pu30'];
const SPLIT_OPTIONS = SPLITS.map(s => ({ value: s.value, label: s.label }));

// ROO2-283: the user account keeps the column blocks; the choice of this browser from before (brief 3.8) moves there once.
const LEGACY_COLUMNS_KEY = 'roompulse.bi.guestMix.columns';

// The Guest Mix report (BI report 4, direction B of the canvas of 2026-10-21): who stays at the hotel, split by guest type,
// nationality, length of stay or lead time, and how the mix moved against STLY. One period, "Stay months", and the revenue
// switches of the Segmentation report. The split is in the address (?split=, decision 18).
const BiGuestMixPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const [params] = useSearchParams();
	const split: GuestMixSplit = splitOf(params.get('split'));
	const [sets, toggle] = useColumnSets('GUEST_MIX', ALL_SETS, DEFAULT_SETS, LEGACY_COLUMNS_KEY);
	const width = useBiWidthClass();
	// ROO2-265: the evolution card (Build-up by default, RN, 90 days, decision 17).
	const [view, setView] = useState<EvolutionView>('BUILD_UP');
	const [measure, setMeasure] = useState<EvolutionMeasure>('rn');
	const [windowDays, setWindowDays] = useState(90);
	// The row whose chart is open; a change of the split, the period or the property closes it.
	const [selected, setSelected] = useState<{ key: string, of: string } | null>(null);
	const toaster = useToaster();
	// true while the Excel file downloads: Export shows "Preparing…" and is disabled.
	const [exporting, setExporting] = useState(false);
	// false until the scope and the Property groups are known, so that a link with ?group= asks for no report of All properties first.
	const enabled = scope.data !== undefined && address.ready;
	const report = useGuestMixReport(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, split, address.includeOptions, enabled);
	const evolution = useGuestMixEvolution(address.companyHotelId, address.propertyGroupId, address.period.from, address.period.to, split, windowDays,
		address.includeOptions, enabled);
	const { first, last } = address;
	const presets = useMemo(() => periodPresets(address.asOfMonth, first, last), [address.asOfMonth, first, last]);
	const s = scope.data;
	// More than one property: All properties or a Property group (the table and the file have property rows).
	const all = address.companyHotelId === null;
	const scopeR = address.revenueScope, vat = address.vat;
	const nameOf = useMemo(() => (id: number) => {
		const p = s?.properties.find(x => x.companyHotelId === id);
		return p === undefined || s === undefined ? String(id) : shortName(p, s.companyName);
	}, [s]);
	// A dimmed earlier answer of another split is not drawn with the words of this split.
	const data = report.data?.split === split ? report.data : undefined;
	const table = useMemo(() => data === undefined ? undefined : buildTable(data, nameOf, all), [data, nameOf, all]);
	const top = useMemo(() => table === undefined ? undefined : headline(table), [table]);
	// A change of the property, the Property group, the period or the split closes the opened rows.
	const resetKey = `${address.companyHotelId ?? `all${address.propertyGroupId ?? ''}`}|${address.period.from}|${address.period.to}|${split}`;
	const open = selected !== null && selected.of === resetKey ? selected.key : null;

	if (scope.isError) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}>
		<Message state="error" title="The Guest Mix report did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	if (s === undefined) return <main className={`${width} px-4 py-6 sm:px-6 lg:px-8`}><DashboardPlaceholder /></main>;

	// The properties of the selection: one property, the properties of a Property group, or all properties.
	const withData = address.properties.some(p => p.reservationsSource !== null);
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	const sublabel = all ? `${propertyCount(address.properties.length)} · Guest Mix` : `${address.property?.area ?? ''} · Guest Mix`;
	// The name of the scope in the file name: the property, the Property group, or null (All properties).
	const scopeName = address.property !== null ? shortName(address.property, s.companyName) : (address.propertyGroup?.title ?? null);
	const loading = report.data === undefined || (report.data.split !== split);
	const state = report.isError ? 'error' as const : (loading ? 'loading' as const : (report.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	const evolutionState = evolution.isError ? 'error' as const
		: (evolution.data === undefined || evolution.data.split !== split ? 'loading' as const : (evolution.isPlaceholderData ? 'refreshing' as const : 'default' as const));
	// "Could not load" with the reason. GraphQLExecutor rejects with the message of the first GraphQL error.
	const reasonOf = (e: unknown): string | null => typeof e === 'string' ? e : (e instanceof Error ? e.message : null);
	// "N guest types · X of Y properties" (or the property's name).
	const readProperties = data === undefined ? 0 : new Set(data.parts.map(p => p.companyHotelId)).size;
	const scopeLine = table === undefined ? null
		: `${table.rows.length} ${splitInfo(split).noun} · ${all ? `${readProperties} of ${propertyCount(address.properties.length)}` : nameOf(address.companyHotelId as number)}`;
	// Like for like: the properties with no stays in the last-year period stay out of Δ% and MIX Δ. ROO2-271 D1: the table then
	// shows TODAY LFL RN, REV and MIX %, and the headline says "like-for-like".
	const missingNames = data === undefined ? [] : data.lastYearMissing.map(nameOf);
	const lfl = missingNames.length > 0;
	// ROO2-315 part c2 (Q4): the report rules, once, for a selection with rules; ADR then divides by the occupied rooms.
	const rulesNote = data === undefined ? null : adrRulesNote(scopeRulesNote(data.reportRules, data.rulesDiffer, address.properties.length));
	const lastYearNote = !lfl ? null
		: `Like-for-like = today without ${missingNames.length === 1 ? 'a property' : `${missingNames.length} properties`} with no stays in the last-year period: ${missingNames.join(', ')}. TODAY LFL RN, TODAY LFL REV and TODAY LFL MIX % show it; Δ% and MIX Δ compare it with last year.`;
	// The Excel file holds the table as the page shows it (decision 8). Export waits for the table: it is off while the table
	// loads, shows the old period dimmed, or failed.
	const exportReady = table !== undefined && !report.isPlaceholderData && !report.isError && !exporting;
	const exportFile = () => {
		setExporting(true);
		BiService.downloadGuestMixExcel({
			companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, stayFrom: address.period.from, stayTo: address.period.to,
			split, scope: scopeR, vat, columns: exportColumns(sets), includeOptions: address.includeOptions,
		})
			.then(blob => saveFile(blob, exportFileName(scopeName, address.period, s.asOf, 'Guest Mix')))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};

	const evolutionData = evolution.data?.split === split ? evolution.data : undefined;

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Guest Mix" fullWidthSwitch>
			{address.importedAt !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{formatDateTime(address.importedAt)}</span></span>}
		</BiPageHeader>
		<main className={`${width} flex flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8`}>
			{!withData && <Message state="info" title="No reservations for this scope" flush>
				<p>The Guest Mix report reads the guests of the imported reservations. Connect a Reservations integration in{' '}
					<Link to={all ? '/secured/settings/integrations' : `/secured/settings/integrations/${address.companyHotelId ?? ''}#reservations`}
						className="font-medium underline">Settings › Integrations</Link>.</p>
			</Message>}
			{withData && <>
				<BiSwitchRow scope={scopeR} vat={vat} onScope={v => address.set({ revenue: v === 'TOTAL' ? 'total' : 'room' })}
					onVat={v => address.set({ vat: v === 'NET' ? 'net' : 'incl' })} options={address.options} onOptions={address.setOptions}
					period={address.period}
					onPeriod={range => address.set({ from: range.from, to: range.to })} presets={presets} min={first} max={last}
					line={scopeLine} sources={sources}>
					<CaptionGroup caption="Split by" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
						<div className="w-full min-w-0"><ButtonTabs label="Split the guests by" value={split}
							onChange={v => address.set({ split: splitInfo(v as GuestMixSplit).param })} options={SPLIT_OPTIONS} /></div>
					</CaptionGroup>
				</BiSwitchRow>
				<GuestMixHeadline headline={report.isError ? { gain: null, loss: null } : top} lfl={lfl} />
				<GuestMixMixCard table={table} split={split} period={address.period} state={state} onRetry={() => void report.refetch()} error={reasonOf(report.error)} />
				<GuestMixTableCard rulesNote={rulesNote} table={table} split={split} period={address.period} sets={sets} onToggle={toggle} scope={scopeR} vat={vat} state={state}
					onRetry={() => void report.refetch()} resetKey={resetKey} lastYearNote={lastYearNote} lfl={lfl}
					onExport={exportFile} exporting={exporting} exportReady={exportReady} error={reasonOf(report.error)} />
				<GuestMixEvolutionCard evolution={evolutionData} table={table} split={split} period={address.period} view={view} onView={setView}
					measure={measure} onMeasure={setMeasure} window={windowDays} onWindow={setWindowDays}
					selected={open} onSelect={key => setSelected(key === null ? null : { key, of: resetKey })}
					scope={scopeR} vat={vat} state={evolutionState} error={reasonOf(evolution.error)} onRetry={() => void evolution.refetch()} />
				{view === 'BUILD_UP' && open !== null && evolutionData !== undefined && table !== undefined
					&& <GuestMixFocusCard evolution={evolutionData} table={table} rowKey={open} period={address.period} measure={measure} onMeasure={setMeasure}
						scope={scopeR} vat={vat} onClose={() => setSelected(null)} />}
			</>}
		</main>
	</>;
};

export default BiGuestMixPage;
